import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { FoundationAssemblyEntityDatums } from "building-types/foundations/foundationAssemblyEntityDatumsTypes";
import type { FoundationOverride } from "building-types/foundations/foundationSystemTypes";
import type { ConstraintLine } from "building-types/framing/floorFramingConstraintTypes";
import type { RoomAttributes } from "building-types/rooms/staticRoomAttributes";
import type { Table } from "common/data/table";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { FramingConfig } from "../framing/framingMetaDataTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { SpanDirection, StructuralFloorType } from "./floorComponentsTypes";
import type { FloorBay, FloorSplitData, FramingOverrideFields } from "./floorSplitDataTypes";

export interface FloorAssemblyBase<T extends FloorAssemblyType> {
  readonly assemblyType: T;
}

export interface SolidFloorAssemblyBase<T extends StructuralFloorType>
  extends FloorAssemblyBase<FloorAssemblyType.SOLID> {
  readonly assemblyId: ForeignEntityId<"FRAME_FLOOR_ASSEMBLY" | "SLAB_FLOOR_ASSEMBLY">;
  readonly structuralType: T;
  readonly subFloor: ForeignEntityId<"SHEATHING_COMPONENT">;
  readonly topDatumOffset?: number;
}

export interface SolidFramingFloorAssembly
  extends SolidFloorAssemblyBase<StructuralFloorType.FRAMING>,
    Pick<FramingOverrideFields, "spanDirection" | "framingAngle" | "framingStartPt"> {
  readonly floorJoist: ForeignEntityId<"FRAMING_COMPONENT">;
  readonly rimBoard: ForeignEntityId<"FRAMING_COMPONENT">;
}
export interface SolidSlabFloorAssembly extends SolidFloorAssemblyBase<StructuralFloorType.SLAB> {
  readonly slab: ForeignEntityId<"SLAB_COMPONENT">;
}

export type SolidFloorAssembly = SolidFramingFloorAssembly | SolidSlabFloorAssembly;

export type FloorAssembly = SolidFloorAssembly | VoidFloorAssembly;

export type VoidFloorAssembly = FloorAssemblyBase<FloorAssemblyType.VOID>;

export enum FloorAssemblyType {
  SOLID = "SOLID",
  VOID = "VOID",
}

export interface FloorEdgeOverride {
  readonly offset?: number;
  readonly foundationEdgeOverride?: FoundationOverride;
  readonly framingConfig?: FramingConfig;
}

export type FloorEdgeOverridesTable = Table<FloorEdgeOverride>;

export interface FloorBoundaryFields {
  readonly floorEdgeOverrides?: FloorEdgeOverridesTable;
  readonly floorOpeningEdgeOverrides?: FloorEdgeOverridesTable;
  readonly perimeters?: PolyLine2[];
  readonly openings?: PolyLine2[];
  readonly edgeIds?: string[];
  readonly perimeterEdgeCounts?: number[];
  readonly openingEdgeIds?: string[];
  readonly openingEdgeCounts?: number[];
  readonly splitData?: FloorSplitData;
}

export interface GeneratedFloorBoundaries {
  readonly floorEdgeOverrides: FloorEdgeOverridesTable;
  readonly floorOpeningEdgeOverrides: FloorEdgeOverridesTable;
  readonly perimeters: PolyLine2[];
  readonly openings: PolyLine2[];
  readonly edgeIds: string[];
  readonly perimeterEdgeCounts: number[];
  readonly openingEdgeIds: string[];
  readonly openingEdgeCounts: number[];
  readonly splitData: FloorSplitData;
  /** For bay boundaries: the parent floor entity ID this bay was split from. */
  readonly parentFloorId?: string;
  /** For bay boundaries: the deterministic bay ID (matches key in splitData.bays). */
  readonly bayId?: FloorBay["id"];
  /** For bay boundaries: constraint lines derived from split line edges for framing. */
  readonly splitLineConstraints?: ConstraintLine[];
}

export enum HorizontalFloorAssemblyType {
  LEGACY = "LEGACY",
  FREE_FORM = "FREE_FORM",
  ROOM_DERIVED = "ROOM_DERIVED",
}

export interface HorizontalFloorAssemblyBase<T extends HorizontalFloorAssemblyType> {
  readonly horizontalAssemblyType: T;
  readonly levelId?: ForeignEntityId<"LEVEL">;
  readonly lateralOffset?: number;
  readonly disableFoundationAssembly?: boolean;
  readonly foundationAssemblyId?: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
  readonly foundationAssemblyEntityDatums?: FoundationAssemblyEntityDatums;
  readonly topDatum?: ForeignEntityId<"DATUM">;
  readonly splitData?: FloorSplitData;
}

export interface HorizontalFloorAssemblyFreeForm
  extends HorizontalFloorAssemblyBase<HorizontalFloorAssemblyType.FREE_FORM>,
    FloorBoundaryFields {}

export interface HorizontalFloorAssemblyRoomDerived
  extends HorizontalFloorAssemblyBase<HorizontalFloorAssemblyType.ROOM_DERIVED>,
    Pick<FloorBoundaryFields, "floorEdgeOverrides" | "floorOpeningEdgeOverrides"> {
  /**
   * Explicit room references used when `allRoomsOnLevel` is not set. The floor
   * boundary is the polygon union of these rooms' outlines.
   */
  readonly roomIds?: readonly ForeignEntityId<"ROOM">[];
  /**
   * When true, the floor boundary is derived from every room on the host level,
   * minus `excludedRoomIds`. Rooms added to the level later are auto-included.
   */
  readonly allRoomsOnLevel?: boolean;
  /**
   * Rooms suppressed from an `allRoomsOnLevel` floor. They remain visible in the
   * inspector with a restore option but do not contribute to the boundary.
   */
  readonly excludedRoomIds?: readonly ForeignEntityId<"ROOM">[];
}

export interface HorizontalFloorAssemblyLegacy
  extends HorizontalFloorAssemblyBase<HorizontalFloorAssemblyType.LEGACY>,
    FloorBoundaryFields {}

export type HorizontalFloorAssembly =
  | HorizontalFloorAssemblyFreeForm
  | HorizontalFloorAssemblyRoomDerived
  | HorizontalFloorAssemblyLegacy;

export interface FloorAssemblyOverrideBase extends FormulaDisabledDocEntityBase<"FLOOR">, LabelableEntityBase {
  readonly id: PrimaryEntityId<"FLOOR">;
  readonly parentId: DocEntityParentId<"ROOM" | "FLOOR_SYSTEM">;
}

export type SolidFramingFloorAssemblyOverride = Partial<SolidFramingFloorAssembly> &
  FloorAssemblyBase<FloorAssemblyType.SOLID> &
  FloorAssemblyOverrideBase &
  HorizontalFloorAssembly;

export type SolidSlabFloorAssemblyOverride = Partial<SolidSlabFloorAssembly> &
  FloorAssemblyBase<FloorAssemblyType.SOLID> &
  FloorAssemblyOverrideBase &
  HorizontalFloorAssembly;

export type VoidFloorAssemblyOverride = Partial<VoidFloorAssembly> &
  FloorAssemblyBase<FloorAssemblyType.VOID> &
  FloorAssemblyOverrideBase &
  HorizontalFloorAssembly;

export type SolidFloorAssemblyOverride = SolidFramingFloorAssemblyOverride | SolidSlabFloorAssemblyOverride;

export type FloorAssemblyOverride = SolidFloorAssemblyOverride | VoidFloorAssemblyOverride;

export type FloorAssemblyHashParameters = Required<
  Pick<
    SolidFloorAssembly & { readonly spanDirection: SpanDirection | "INHERIT" },
    "assemblyType" | "topDatumOffset" | "assemblyId" | "spanDirection"
  > &
    Pick<RoomAttributes, "roomClass" | "roomEnclosure" | "openToBelow">
>;
